import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Recycle, TrendingDown, Clock, Coins, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const REUSE_COMPOSITION = [
    { asset: "Data Sheets Generated", units: 642, hours: 32100, saving: 4.82 },
    { asset: "Calculations Reused",   units: 321, hours: 24800, saving: 3.72 },
    { asset: "PFDs Reused",           units: 88,  hours: 9600,  saving: 1.44 },
    { asset: "Drawings Reused",       units: 412, hours: 41200, saving: 6.18 },
    { asset: "Specifications Reused", units: 76,  hours: 6200,  saving: 0.93 },
    { asset: "Vendor Packages Reused",units: 145, hours: 18200, saving: 2.73 },
    { asset: "Templates Applied",     units: 612, hours: 24800, saving: 3.72 },
    { asset: "Procurement Packages",  units: 118, hours: 14400, saving: 2.16 },
    { asset: "Bid Time Saved",        units: 42,  hours: 6720,  saving: 1.01 },
    { asset: "Engineering Packages",  units: 218, hours: 40380, saving: 6.05 },
];

function fmtInt(n) { return n.toLocaleString(); }
function fmtUsd(n) { return `$${n.toFixed(2)}M`; }

export default function ReuseSavings() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/edi/summary").then((r) => setData(r.data)).catch(() => toast.error("Failed to load reuse savings"));
    }, []);
    if (!data) return <div className="kicker text-slate-500">Loading reuse savings…</div>;

    const totalHours = REUSE_COMPOSITION.reduce((s, r) => s + r.hours, 0);
    const totalSaving = REUSE_COMPOSITION.reduce((s, r) => s + r.saving, 0);
    const engineerYears = (totalHours / 1800).toFixed(1);

    return (
        <div className="space-y-8" data-testid={EDI.reuseSavingsRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Reuse Savings Dashboard
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Where knowledge reuse is landing in engineer-hours, engineer-years and estimated cost avoidance.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">YTD · Feb 2026</span>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    ["Engineering Hours Saved", fmtInt(totalHours),                Clock,       "Portfolio, YTD"],
                    ["Engineer Years Saved",    engineerYears,                    Coins,       "Equivalent FTEs · 1,800 hrs / yr"],
                    ["Estimated Cost Saved",    fmtUsd(totalSaving),              Coins,       "Portfolio impact"],
                    ["Rework Reduction",        "-66.8%",                          TrendingDown,"vs. baseline"],
                ].map(([k, v, Icon, s]) => (
                    <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="mt-2 kicker text-slate-400 text-[10px]">{k}</div>
                        <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                        <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl p-6 xl:col-span-2">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <Recycle className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Composition</span>
                    </div>
                    <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">Hours saved by asset class</h3>
                    <div className="h-[360px] mt-4 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={REUSE_COMPOSITION} layout="vertical" margin={{ left: 30 }}>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" horizontal={false} />
                                <XAxis type="number" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis dataKey="asset" type="category" stroke="#64748B" width={180} tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Bar dataKey="hours" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="glass rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-slate-800/60">
                        <h3 className="font-display font-black text-white tracking-tight">Detailed savings</h3>
                    </div>
                    <div className="max-h-[380px] overflow-y-auto">
                        <table className="w-full text-sm font-body">
                            <thead className="sticky top-0 bg-slate-900/95 backdrop-blur">
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Asset</th>
                                    <th className="text-right px-4 py-3">Units</th>
                                    <th className="text-right px-4 py-3">Hours</th>
                                    <th className="text-right px-4 py-3">$M</th>
                                </tr>
                            </thead>
                            <tbody>
                                {REUSE_COMPOSITION.map((r) => (
                                    <tr key={r.asset} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                        <td className="px-4 py-2.5 text-slate-200">{r.asset}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-300">{fmtInt(r.units)}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-100 font-medium">{fmtInt(r.hours)}</td>
                                        <td className="px-4 py-2.5 text-right text-[var(--sgsb-gold)] font-display font-bold">{r.saving.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
